/**
 * MC mega menu (desktop).
 *
 * Disclosure-pattern mega menu: each top-level item with children has a button
 * trigger (aria-expanded / aria-controls) and a full-width panel under the header row.
 *
 * - Hover intent: opens after OPEN_DELAY ms, closes after CLOSE_DELAY ms grace.
 * - Click / Enter / Space toggle; ArrowDown opens and moves focus into the panel.
 * - Escape closes and returns focus to the trigger.
 * - Only one panel open; closes on outside click, focus leaving, scroll-away and resize.
 */

const OPEN_DELAY = 120;
const SWITCH_DELAY = 60;
const CLOSE_DELAY = 200;
const SCROLL_CLOSE_DISTANCE = 60;
const DESKTOP_QUERY = '(min-width: 750px)';

class McMegaMenu extends HTMLElement {
  /** @type {HTMLElement | null} */
  #openItem = null;
  /** @type {'hover' | 'click' | 'keyboard' | null} */
  #openedBy = null;
  #openedAt = 0;
  #scrollStart = 0;
  /** @type {number | undefined} */
  #openTimer;
  /** @type {number | undefined} */
  #closeTimer;
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {MutationObserver | null} */
  #sourceObserver = null;

  connectedCallback() {
    this.#applyCountdowns();
    this.#setupCarousels();
    this.#placePromos();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // If the header markup is morphed back to server HTML, cards reappear in the source: place them again.
    const source = this.querySelector('[data-mc-mega-promo-source]');
    if (source) {
      this.#sourceObserver = new MutationObserver(() => {
        if (source.querySelector('[data-mc-mega-for]')) this.#placePromos();
      });
      this.#sourceObserver.observe(source, { childList: true, subtree: true });
    }

    /** @type {HTMLElement[]} */
    this.items = Array.from(this.querySelectorAll('[data-mc-mega-item]')).filter(
      (item) => item instanceof HTMLElement && item.querySelector('[data-mc-mega-panel]')
    );

    for (const item of this.items) {
      const trigger = this.#trigger(item);
      if (!trigger) continue;

      item.addEventListener('pointerenter', (event) => this.#onPointerEnter(event, item), { signal });
      item.addEventListener('pointerleave', (event) => this.#onPointerLeave(event), { signal });
      trigger.addEventListener('click', (event) => this.#onTriggerClick(event, item), { signal });
      trigger.addEventListener('keydown', (event) => this.#onTriggerKeydown(event, item), { signal });
    }

    // Warm panels (render them hidden) on first intent so lazy images start loading.
    this.addEventListener('pointerenter', () => this.#warm(), { signal, once: true });
    this.addEventListener('focusin', () => this.#warm(), { signal, once: true });

    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    this.querySelector('[data-mc-mega-scrim]')?.addEventListener('click', () => this.close(), { signal });

    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
    window.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    window.addEventListener('resize', () => this.close(), { signal });

    // Theme editor: open the panel that holds a selected promo card.
    document.addEventListener('shopify:block:select', this.#onEditorSelect, { signal });
    document.addEventListener('shopify:block:deselect', () => this.close(), { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#sourceObserver?.disconnect();
    this.#clearTimers();
  }

  /**
   * Snap carousels inside panels (Events): arrows scroll by most of a view and switch off
   * at either end. Cards stay reachable by Tab; the track also scrolls with arrow keys.
   */
  #setupCarousels() {
    for (const track of this.querySelectorAll('[data-mc-mega-carousel]')) {
      if (!(track instanceof HTMLElement)) continue;
      const scope = track.closest('.mc-mega-events') || track.parentElement;
      const prev = scope?.querySelector('[data-mc-mega-carousel-prev]');
      const next = scope?.querySelector('[data-mc-mega-carousel-next]');
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev instanceof HTMLButtonElement) prev.disabled = track.scrollLeft <= 2;
        if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= max;
        const arrows = scope?.querySelector('[data-mc-mega-carousel-arrows]');
        if (arrows instanceof HTMLElement) arrows.hidden = track.scrollWidth <= track.clientWidth + 2;
      };
      const step = (dir) => track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      prev?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));
      track.addEventListener('scroll', sync, { passive: true });
      new ResizeObserver(sync).observe(track);
      sync();
    }
  }

  /**
   * Campaign cards carry data-mc-mega-ends (YYYY-MM-DD, end of that day UK time): fill the
   * "Closes in 27 days" chip, or drop the card once the date has passed (cached pages too).
   */
  #applyCountdowns() {
    const now = new Date();
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    for (const card of this.querySelectorAll('[data-mc-mega-ends], [data-mc-mega-starts]')) {
      const ends = card.getAttribute('data-mc-mega-ends') || '';
      const starts = card.getAttribute('data-mc-mega-starts') || '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(starts) && now < new Date(`${starts}T00:00:00Z`)) {
        card.remove();
        continue;
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ends)) continue;
      const end = new Date(`${ends}T23:59:59Z`);
      if (now > end) {
        card.remove();
        continue;
      }
      const days = Math.round((Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()) - today) / 864e5);
      const phrase = days === 0 ? 'today' : days === 1 ? 'tomorrow' : `${days} days`;
      let text = (card.getAttribute('data-mc-mega-countdown-label') || 'Closes in [days]').replace('[days]', phrase);
      if (days <= 1) text = text.replace(/\bin (today|tomorrow)\b/, '$1');
      const chip = card.querySelector('[data-mc-mega-countdown]');
      if (chip instanceof HTMLElement) {
        chip.textContent = text;
        chip.hidden = false;
      }
    }
  }

  /**
   * Promo cards render once in a hidden source container (Liquid `content_for 'blocks'`).
   * Move each card into its panel slot (max two per panel) and clone every card into
   * the mobile drawer highlights carousel. Slots without cards stay hidden.
   */
  #placePromos() {
    const source = this.querySelector('[data-mc-mega-promo-source]');
    if (!source) return;

    const cards = Array.from(source.querySelectorAll('[data-mc-mega-for]'));
    if (!cards.length) return;

    // Reset slots so a re-run (after the header is re-rendered) never duplicates cards.
    const highlightSlots = document.querySelectorAll('[data-mc-mega-drawer-slot="__all"]');
    for (const slot of highlightSlots) slot.replaceChildren();
    for (const slot of this.querySelectorAll('[data-mc-mega-slot]')) {
      slot.replaceChildren();
      if (slot instanceof HTMLElement) slot.hidden = true;
    }

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const key = card.dataset.mcMegaFor;
      if (!key) continue;

      // Mobile drawer: every card goes into one highlights carousel at the top.
      for (const slot of highlightSlots) {
        const li = document.createElement('li');
        li.className = 'mc-mega-drawer__promo';
        const copy = /** @type {HTMLElement} */ (card.cloneNode(true));
        copy.removeAttribute('data-shopify-editor-block');
        li.append(copy);
        slot.append(li);
      }

      const panelSlot = this.querySelector(`[data-mc-mega-slot="${CSS.escape(key)}"]`);
      const limit = panelSlot instanceof HTMLElement ? Number(panelSlot.dataset.mcMegaLimit) || 2 : 0;
      if (panelSlot instanceof HTMLElement && panelSlot.children.length < limit) {
        panelSlot.append(card);
        panelSlot.hidden = false;
      } else {
        card.remove();
      }
    }

    for (const slot of highlightSlots) {
      const wrapper = slot.closest('[data-mc-mega-drawer-highlights]');
      if (wrapper instanceof HTMLElement) wrapper.hidden = slot.children.length === 0;
    }
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #trigger(item) {
    return item.querySelector('[data-mc-mega-trigger]');
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #panel(item) {
    return item.querySelector('[data-mc-mega-panel]');
  }

  #warm() {
    this.dataset.warm = '';
  }

  #clearTimers() {
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
  }

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} item
   */
  #onPointerEnter(event, item) {
    if (event.pointerType !== 'mouse') return;
    if (!window.matchMedia(DESKTOP_QUERY).matches) return;

    this.#warm();
    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#openTimer);

    if (this.#openItem === item) return;

    const delay = this.#openItem ? SWITCH_DELAY : OPEN_DELAY;
    this.#openTimer = window.setTimeout(() => this.open(item, 'hover'), delay);
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerLeave(event) {
    if (event.pointerType !== 'mouse') return;

    window.clearTimeout(this.#openTimer);
    if (!this.#openItem || this.#openedBy !== 'hover') return;

    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.close(), CLOSE_DELAY);
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLElement} item
   */
  #onTriggerClick(event, item) {
    event.preventDefault();
    this.#clearTimers();

    // A click right after a hover-open confirms the panel instead of closing it.
    const justOpenedByHover = this.#openedBy === 'hover' && Date.now() - this.#openedAt < 600;

    if (this.#openItem === item && !justOpenedByHover) {
      this.close();
      return;
    }

    // event.detail is 0 for keyboard-triggered clicks (Enter / Space).
    this.open(item, event.detail === 0 ? 'keyboard' : 'click');
  }

  /**
   * @param {KeyboardEvent} event
   * @param {HTMLElement} item
   */
  #onTriggerKeydown(event, item) {
    if (event.key !== 'ArrowDown') return;

    event.preventDefault();
    this.open(item, 'keyboard');
    const firstLink = this.#panel(item)?.querySelector('a[href], button:not([disabled])');
    if (firstLink instanceof HTMLElement) firstLink.focus();
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#openItem) return;

    const trigger = this.#trigger(this.#openItem);
    this.close();
    trigger?.focus();
  };

  /**
   * Close the open panel as soon as focus moves into a different top-level item.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    if (!this.#openItem) return;
    if (event.target instanceof Node && !this.#openItem.contains(event.target)) this.close();
  };

  /**
   * Close when focus leaves the menu entirely.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    if (!this.#openItem) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    if (next === null && this.#openedBy === 'hover') return;
    this.close();
  };

  /**
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    if (!this.#openItem) return;
    const target = event.target;
    if (target instanceof Node && this.#openItem.contains(target)) return;
    if (target instanceof Element && target.closest('[data-mc-mega-scrim]')) return;
    this.close();
  };

  #onScroll = () => {
    if (!this.#openItem) return;
    if (Math.abs(window.scrollY - this.#scrollStart) > SCROLL_CLOSE_DISTANCE) this.close();
  };

  /**
   * @param {Event} event
   */
  #onEditorSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const item = target.closest('[data-mc-mega-item]');
    if (item instanceof HTMLElement && this.contains(item)) {
      this.#warm();
      this.open(item, 'click');
    }
  };

  /**
   * Measure where the header row ends so the panel and scrim sit flush under it.
   */
  #measure() {
    const row = this.closest('.header__row') || this.closest('header-component');
    if (!(row instanceof HTMLElement)) return;
    const bottom = Math.max(0, Math.round(row.getBoundingClientRect().bottom));
    this.style.setProperty('--mc-mega-top', `${bottom}px`);

    // header-component uses `contain: layout`, so it is the containing block for the
    // fixed scrim: offset the scrim relative to the header box, not the viewport.
    const header = this.closest('header-component');
    const headerTop = header instanceof HTMLElement ? Math.round(header.getBoundingClientRect().top) : 0;
    this.style.setProperty('--mc-mega-scrim-top', `${bottom - headerTop}px`);
  }

  /**
   * @param {HTMLElement} item
   * @param {'hover' | 'click' | 'keyboard'} by
   */
  open(item, by) {
    const panel = this.#panel(item);
    const trigger = this.#trigger(item);
    if (!panel || !trigger) return;

    this.#clearTimers();

    const switching = Boolean(this.#openItem && this.#openItem !== item);
    if (this.#openItem && this.#openItem !== item) this.#closeItem(this.#openItem);
    this.toggleAttribute('data-switching', switching);

    this.#measure();
    this.#warm();

    if (this.#openItem !== item) {
      this.#openedAt = Date.now();
    }

    this.#openItem = item;
    this.#openedBy = by;
    this.#scrollStart = window.scrollY;

    trigger.setAttribute('aria-expanded', 'true');
    item.dataset.open = '';
    panel.dataset.open = '';
    this.dataset.open = '';
  }

  close() {
    this.#clearTimers();
    if (!this.#openItem) return;
    this.#closeItem(this.#openItem);
    this.#openItem = null;
    this.#openedBy = null;
    delete this.dataset.open;
    delete this.dataset.switching;
  }

  /**
   * @param {HTMLElement} item
   */
  #closeItem(item) {
    this.#trigger(item)?.setAttribute('aria-expanded', 'false');
    delete item.dataset.open;
    const panel = this.#panel(item);
    if (panel) delete panel.dataset.open;
  }
}

if (!customElements.get('mc-mega-menu')) {
  customElements.define('mc-mega-menu', McMegaMenu);
}
